import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Calendar } from "@untitledui/icons";
import { type LocationComputedFields, fromWireSubmission, getSubmission, putRelativity, toWireRelativity } from "../../../api";
import { FeaturedAlert } from "../../../components/featured-alert";
import { SubmissionFooter } from "../../../components/submission-footer";
import { useFacilities } from "../../../hooks/use-facilities";
import { useRates } from "../../../hooks/use-rates";
import { useSirCredits } from "../../../hooks/use-sir-credits";
import { useSubmissionContext } from "../context";
import { getNextSection } from "../sections";
import { type ScheduleFormData, scheduleSchema } from "./schema";
import { useFacilitiesCalc } from "./use-facility-calc";
import { FacilityScheduleSection } from "./sections/facility-schedule";
import { RelativitySection } from "./sections/relativity";
import { StateDistributionSection } from "./sections/state-distribution";
import { SummaryCards } from "./sections/summary-cards";
import { useScheduleAggregates } from "./use-schedule-aggregates";

/** Shared mutable handle: the binder (inside the FormProvider) keeps it
 *  filled with each facility's FE-computed pricing values; `persist()`
 *  (outside the provider) reads it at save time to ride them along on
 *  the location writes. A ref avoids re-rendering the page on recompute. */
type ComputedRef = { current: Map<string, LocationComputedFields> };

/** Stable empty reference so the binder's memo doesn't churn while the
 *  facilities are still loading. */
const EMPTY_FACILITIES: never[] = [];
// NOTE: Schedule derived values (occupancy, equivalency, premiums, …)
// are computed client-side via `useFacilitiesCalc` + `useScheduleAggregates`
// (pure functions in `domain/pricing`). The old backend-calc path
// (`useCalculations` → /v1/calculator/*) was removed in the v1→v2 cleanup;
// if calcs ever move server-side, defer to /v2/submissions/{id}/quote/calculate.

const SECTION_ID = "schedule";

export function SchedulePage() {
    const { detail, setDetail, scheduleHasWarning, markBorReceived } = useSubmissionContext();
    const navigate = useNavigate();
    const [isSaving, setIsSaving] = useState(false);

    // Per-facility FE-computed pricing values, kept current by the binder
    // below and read at save time so they ride along on the location writes.
    const computedRef = useRef<Map<string, LocationComputedFields>>(new Map());
    // Whether the pricing tables behind those values have loaded (OR-1654).
    const pricingReadyRef = useRef(false);

    const methods = useForm<ScheduleFormData>({
        resolver: zodResolver(scheduleSchema),
        defaultValues: detail.schedule,
        // onTouched — see the equivalent comment on the UW
        // Information page; we want immediate inline feedback once
        // the user has touched a field.
        mode: "onTouched",
    });

    // Save flushes both halves of the Schedule: the Facility table
    // (staged in `useFacilities` → `/v2/submissions/{id}/locations`) and
    // the relativity row (→ PUT /v2/submissions/{id}/relativity/). The
    // aggregates (rate* / stateDistribution) are computed, not persisted.
    const { state: facilitiesState, save: saveFacilities, isDirty: facilitiesDirty, reload: reloadFacilities } = useFacilities(detail.id);

    // OR-1297-style cross-tab refresh: the facilities store caches per submission
    // and only loads once, so a facility created elsewhere (e.g. via the Acuity
    // Add Row → direct POST, which bypasses this store) wouldn't show here until
    // a hard refresh. Re-fetch once per navigation into Schedule (background, no
    // loading flash), skipped while there are unsaved staged edits.
    useEffect(() => {
        if (!detail.id || facilitiesDirty) return;
        void reloadFacilities();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail.id]);

    // Duplicate-facility counts for the warning banner copy. A facility
    // is flagged when the backend sets its `duplicateSubmissionId`.
    const facilitiesList = facilitiesState.status === "ready" ? facilitiesState.data : [];
    const duplicateCount = facilitiesList.filter((f) => f.duplicateSubmissionId).length;
    const facilityCount = facilitiesList.length;

    const persist = useCallback(
        async (data: ScheduleFormData) => {
            setIsSaving(true);
            try {
                // A Relativity change moves every facility's premium without
                // touching any row, so push the recomputed values for all of
                // them; otherwise Premium Selection keeps the old sum (OR-1654).
                const prev = detail.schedule.relativity;
                const relativityChanged = (Object.keys(data.relativity) as (keyof typeof prev)[]).some(
                    (k) => data.relativity[k] !== prev[k],
                );
                // Until the rates and SIR-credit tables load, the calcs are
                // empty placeholders; syncing them would overwrite every
                // facility's stored premium with zeros, so fail the save instead.
                if (relativityChanged && !pricingReadyRef.current) return false;
                const facilitiesSaved = await saveFacilities({ computedById: computedRef.current, syncComputed: relativityChanged });
                if (!facilitiesSaved) return false;
                try {
                    await putRelativity(detail.id, toWireRelativity(data.relativity));
                } catch (error) {
                    console.warn("putRelativity failed:", error);
                    return false;
                }
                // Re-GET the submission for the server-owned duplicate signal
                // (OR-1442). The backend flips `is_bor_received` to false from
                // inside the location POST when it detects a duplicate, and
                // nothing else in the session ever refetches the detail — so
                // without this the warning banner + nav icon stayed hidden
                // until a hard refresh. Only that one field is merged: the rest
                // of `detail` holds freshly-saved form state we must not clobber.
                let isBorReceived = detail.isBorReceived;
                try {
                    isBorReceived = fromWireSubmission(await getSubmission(detail.id)).isBorReceived;
                } catch (error) {
                    // A failed refresh degrades to the pre-save flag rather
                    // than failing a save that already landed.
                    console.warn("Failed to refresh submission after Schedule save:", error);
                }
                setDetail({ ...detail, schedule: data, isBorReceived });
                methods.reset(data);
                return true;
            } finally {
                setIsSaving(false);
            }
        },
        [detail, methods, setDetail, saveFacilities],
    );

    // Save validates + persists without navigating (Andressa's separate-
    // button flow). Continue just navigates — the footer guards against
    // leaving with unsaved changes.
    const onSave = methods.handleSubmit(
        async (data) => {
            await persist(data);
        },
        () => {
            requestAnimationFrame(() => {
                const first = document.querySelector<HTMLElement>('[aria-invalid="true"]');
                if (!first) return;
                first.scrollIntoView({ behavior: "smooth", block: "center" });
                if (typeof first.focus === "function") first.focus({ preventScroll: true });
            });
        },
    );

    const next = getNextSection(SECTION_ID);
    const onContinue = () => navigate(next ? `../${next.path}` : "/submissions");
    // Dirty when either half (relativity form or the facilities SoT) has
    // unsaved edits.
    const isDirty = methods.formState.isDirty || facilitiesDirty;
    const renewal = detail.accountBasics.renewalDate || "";
    const expiration = detail.accountBasics.expirationDate || "";

    return (
        <FormProvider {...methods}>
            <AggregatesBinder submissionId={detail.id} />
            <ComputedLocationsBinder submissionId={detail.id} computedRef={computedRef} pricingReadyRef={pricingReadyRef} />
            <div className="w-full max-w-dashboard space-y-6">
                {scheduleHasWarning && (
                    <FeaturedAlert
                        title="Duplicate Submission Detected"
                        data-autotest="schedule-page-BOR-alert"
                        description={
                            duplicateCount > 0
                                ? `${duplicateCount} of ${facilityCount} facilities were previously submitted. Check the flags and update the status to keep the submission accurate.`
                                : "One or more facilities were previously submitted. Check the flags and update the status to keep the submission accurate."
                        }
                        primaryAction={{
                            label: "Mark BOR as Received",
                            onClick: () => markBorReceived(),
                        }}
                    />
                )}

                <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="flex flex-col gap-1">
                        <h2 className="text-primary text-md font-semibold">Schedule</h2>
                        <p className="text-tertiary text-sm">
                            Facility locations, operators, retro dates and state exposure
                        </p>
                    </div>
                    {/* Mobile: dates spread 50/50, divider hidden (gap is
                      * enough at small widths). md+: content-width with
                      * divider visible, tight to the page title. */}
                    <div className="flex w-full items-stretch justify-between gap-6 md:w-auto md:shrink-0 md:justify-start">
                        <DateField label="Renewal Date" value={renewal} />
                        <span aria-hidden="true" className="bg-border-secondary hidden w-px self-stretch md:block" />
                        <DateField label="Expiration Date" value={expiration} />
                    </div>
                </header>

                <SummaryCards />
                <RelativitySection />
                <FacilityScheduleSection />
                <StateDistributionSection />
            </div>

            <SubmissionFooter
                onSave={onSave}
                onContinue={onContinue}
                isDirty={isDirty}
                isSaving={isSaving}
                nextSectionLabel={next?.label ?? null}
            />
        </FormProvider>
    );
}

/** Renders nothing — its only job is to call `useScheduleAggregates`
 *  from *inside* the FormProvider tree so it can `useFormContext`. */
function AggregatesBinder({ submissionId }: { submissionId: string | undefined }) {
    useScheduleAggregates(submissionId);
    return null;
}

/** Renders nothing — runs `useFacilitiesCalc` inside the FormProvider
 *  (so it sees the relativity form) and mirrors each facility's derived
 *  pricing values into `computedRef`, keyed by row id, so the page's
 *  `persist()` can send them on the location writes (Henry, jun-23). */
function ComputedLocationsBinder({
    submissionId,
    computedRef,
    pricingReadyRef,
}: {
    submissionId: string | undefined;
    computedRef: ComputedRef;
    pricingReadyRef: { current: boolean };
}) {
    const { state } = useFacilities(submissionId);
    const facilities = useMemo(() => (state.status === "ready" ? state.data : EMPTY_FACILITIES), [state]);
    const calcs = useFacilitiesCalc(facilities);
    // Same session-cached tables `useFacilitiesCalc` reads; no extra request.
    const ratesQuery = useRates();
    const sirCreditsQuery = useSirCredits();

    useEffect(() => {
        pricingReadyRef.current = ratesQuery.status === "ready" && sirCreditsQuery.status === "ready";
    }, [ratesQuery.status, sirCreditsQuery.status, pricingReadyRef]);

    useEffect(() => {
        const map = new Map<string, LocationComputedFields>();
        facilities.forEach((f, i) => {
            const c = calcs[i];
            if (!c) return;
            map.set(f.id, {
                equivalency: c.equivalency,
                actuarial_rate: c.rate,
                unadjusted_premium: c.unadjPremium,
                maturity_adjusted_premium: c.matAdjPremium,
                sir_credit: c.sirCredit,
                sir_adjusted_premium: c.sirAdjPremium,
            });
        });
        computedRef.current = map;
    }, [facilities, calcs, computedRef]);

    return null;
}

function DateField({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="text-tertiary text-xs font-normal">{label}</span>
            <span className="text-secondary inline-flex items-center gap-1.5 text-xs font-semibold">
                <Calendar aria-hidden="true" className="text-fg-quaternary size-4" />
                {value || <span className="text-placeholder font-normal">—</span>}
            </span>
        </div>
    );
}
